import React from "react";
import { DateTime } from "luxon";
import type { Subscription } from "client/api-v2";
import type { EntityHistoryReferenceDto } from "client/api/EntityHistoryReferenceDto";
import { DetailsPageLayout } from "components/DetailsPageLayout/DetailsPageLayout";
import { EntityHistoryView } from "components/EntityHistoryView/EntityHistoryView";
import { useEntity } from "components/EntityProvider/EntityProvider";
import { SubscriptionService } from "areas/subscriptions/services/subscriptionService";

type SubscriptionSnapshot = Record<string, unknown> & {
  licenseEntity?: Record<string, unknown> | null;
  licenseEntityFetchError?: boolean;
};

function projectSubscriptionFields(snapshot: SubscriptionSnapshot): object {
  const { licenseEntityFetchError: _e, ...rest } = snapshot;
  return rest;
}

export function SubscriptionHistory() {
  const { entity: subscription } = useEntity<Subscription>();

  if (!subscription) return <></>;

  const getSubscriptionHistory = async (): Promise<EntityHistoryReferenceDto[]> => {
    if (!subscription.id) {
      throw new Error("Subscription ID is required");
    }
    const historyData = await SubscriptionService.getSubscriptionHistory(subscription.id);

    return historyData.map((item) => ({
      entityId: item.entityId || "",
      entityType: item.entityType || "",
      validFrom: DateTime.fromISO(item.validFrom || ""),
      validTo: DateTime.fromISO(item.validTo || ""),
      lastModifiedByUser: item.lastModifiedByUser || "",
    }));
  };

  const getHistoricalSubscription = async (asOf: DateTime): Promise<SubscriptionSnapshot> => {
    if (!subscription.id) {
      throw new Error("Subscription ID is required");
    }

    const snapshot = await SubscriptionService.getHistorySnapshot(subscription.id, asOf.toISO());

    return {
      ...(snapshot.subscription ?? {}),
      licenseEntity: snapshot.licenseEntity,
      licenseEntityFetchError: snapshot.licenseEntityFetchFailed,
    };
  };

  return (
    <DetailsPageLayout pageName={"History"}>
      <EntityHistoryView
        getHistoricalEntity={getHistoricalSubscription}
        getEntityHistory={getSubscriptionHistory}
        projectFields={projectSubscriptionFields}
      />
    </DetailsPageLayout>
  );
}
